import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { connect } from 'react-redux';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { compose } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';

import Avatar from '@mui/material/Avatar';
import Skeleton from 'core/skeleton/Skeleton';

import { buildRoute } from 'routes';
import isEmpty from 'helpers/isEmpty';
import useCountries from 'hooks/useCountries';
import AuthUserIsSuperAdmin from 'components/auth/AuthUserIsSuperAdmin';
import ActionButton from 'core/buttons/ActionButton';

import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';

const PartnerCardContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(2),
  backgroundColor: 'inherit',
  // border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 15,
  boxShadow: '1px 3px 6px 1px rgba(0,0,0,0.3)',
}));

const PartnerLogo = styled(Avatar)(({ theme }) => ({
  height: '80px',
  width: '80px',
  background: theme.palette.primary.soft,
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightBold,
}));

const PartnerDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const PartnerActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginRight: 'auto',
  flexGrow: 1,
  alignSelf: 'baseline',
  justifyContent: 'end',
}));

function PartnerCard({ isLoading, partner }) {
  const { getCountryNameByIso3Code } = useCountries();

  const navigate = useNavigate();

  const params = useParams();

  if (isLoading)
    return (
      <PartnerCardContainer elevation={0}>
        <Skeleton sx={{ width: '80px', height: '80px', borderRadius: '4px' }} />

        <PartnerDetails sx={{ gap: 1 }}>
          <Skeleton sx={{ width: '250px', height: '32px', borderRadius: '4px' }} />

          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            <Skeleton sx={{ width: '200px', height: '16px', borderRadius: '4px' }} />
          </Stack>
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            <Skeleton sx={{ width: '180px', height: '12px', borderRadius: '10px' }} />
          </Stack>
        </PartnerDetails>
        <PartnerActions>
          <Skeleton sx={{ width: '130px', height: '37px', borderRadius: '4px' }} />
        </PartnerActions>
      </PartnerCardContainer>
    );

  const partnerShortname = partner?.name
    .split(' ')
    ?.map((n) => n[0])
    .join('');

  return (
    <PartnerCardContainer elevation={0}>
      <PartnerLogo variant="circular">{partnerShortname}</PartnerLogo>
      <PartnerDetails sx={{ gap: 0.4 }}>
        <Typography variant="h5" fontWeight={800} letterSpacing={1.5}>
          {partner.name}
        </Typography>
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1}
          sx={{ color: (theme) => theme.palette.grey[600] }}
        >
          <RoomOutlinedIcon sx={{ fontSize: 'inherit' }} />
          <Typography>
            {[partner.city, partner.state, getCountryNameByIso3Code(partner.country)]
              .filter((v) => !!v)
              .join(', ')}
          </Typography>
        </Stack>
        {partner?.post_code && (
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ color: (theme) => theme.palette.grey[600] }}
          >
            <MailOutlineOutlinedIcon sx={{ fontSize: 'inherit' }} />
            <Typography variant="subtitle2">Post Code - {partner.post_code}</Typography>
          </Stack>
        )}
      </PartnerDetails>
      <PartnerActions>
        <HasPermission permission={permissions.UPDATE_PARTNER}>
          {isLoading ? (
            <Skeleton sx={{ width: '130px', height: '37px', borderRadius: '4px' }} />
          ) : (
            <ActionButton onClick={() => navigate(buildRoute('partners.edit', params.partnerId))}>
              Edit Partner
            </ActionButton>
          )}
        </HasPermission>
      </PartnerActions>
    </PartnerCardContainer>
  );
}

export default compose(connect(mapStateToProps))(PartnerCard);

PartnerCard.propTypes = {
  partner: PropTypes.object,
  isLoading: PropTypes.bool,
};

PartnerCard.defaultProps = {
  partner: {},
  isLoading: false,
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    ...(!Object.prototype.hasOwnProperty.call(ownProps, 'partner')
      ? {
          partner: state.partners.show.data || {},
        }
      : {}),
    ...(!Object.prototype.hasOwnProperty.call(ownProps, 'isLoading')
      ? {
          isLoading: state.partners.show.isLoading || isEmpty(state.partners.show.data),
        }
      : {}),
  };
}
